import { Navigate, Route, Routes } from 'react-router-dom';
import { LandingPage } from './pages/landing/landing-page';
import { ChooserPage } from './pages/chooser-page';
import { LoginPage } from './pages/login-page';
import { UserLoginPage } from './pages/user-login-page';
import { RequestAccountDeletionPage } from './pages/request-account-deletion-page';
import { OnboardingShell } from './pages/onboarding-shell';
import { StartPage } from './pages/onboarding/start-page';
import { BasicInfoPage } from './pages/onboarding/basic-info-page';
import { AddressPage } from './pages/onboarding/address-page';
import { UploadsPage } from './pages/onboarding/uploads-page';
import { ModuleCafePage } from './pages/onboarding/module-cafe-page';
import { ModuleWalkerPage } from './pages/onboarding/module-walker-page';
import { ModuleTrainerPage } from './pages/onboarding/module-trainer-page';
import { ModuleGroomerPage } from './pages/onboarding/module-groomer-page';
import { ModuleRestaurantPage } from './pages/onboarding/module-restaurant-page';
import { ModulePharmacyPage } from './pages/onboarding/module-pharmacy-page';
import { ModuleFoodPage } from './pages/onboarding/module-food-page';
import { ModuleAdoptionPage } from './pages/onboarding/module-adoption-page';
import { ModuleCremationPage } from './pages/onboarding/module-cremation-page';
import { ModuleLabPage } from './pages/onboarding/module-lab-page';
import { ModuleVetPage } from './pages/onboarding/module-vet-page';
import { PayoutPage } from './pages/onboarding/payout-page';
import { TermsPage } from './pages/onboarding/terms-page';
import { SubmitPage } from './pages/onboarding/submit-page';
import { VendorShell } from './pages/vendor/vendor-shell';
import { VendorDashboardPage } from './pages/vendor/dashboard-page';
import { VendorServicesPage } from './pages/vendor/services-page';
import { DiningSetupPage } from './pages/vendor/dining-setup-page';
import { DiningOfferingsPage } from './pages/vendor/dining-offerings-page';
import { VendorAvailabilityPage } from './pages/vendor/availability-page';
import { VendorSchedulePage } from './pages/vendor/schedule-page';
import { VendorBookingsPage } from './pages/vendor/bookings-page';
import { VendorWalkPlansPage } from './pages/vendor/walk-plans-page';
import { VendorTrainingPackagesPage } from './pages/vendor/training-packages-page';
import { GroomingPackagesPage } from './pages/vendor/grooming-packages-page';
import { VendorServicePackagesPage } from './pages/vendor/service-packages-page';
import { GroomReportsPage } from './pages/vendor/groom-reports-page';
import { VendorReviewsPage } from './pages/vendor/reviews-page';
import { VendorMenuImagesPage } from './pages/vendor/menu-images-page';
import { VendorOrdersPage } from './pages/vendor/orders-page';
import { VendorPharmacyProductsPage } from './pages/vendor/pharmacy-products-page';
import { VendorFoodProductsPage } from './pages/vendor/food-products-page';
import { CommerceShipmentsPage } from './pages/vendor/commerce-shipments-page';
import { VendorProfilePage } from './pages/vendor/profile-page';
import { VendorReportsPage } from './pages/vendor/reports-page';
import { VendorPaymentsPage } from './pages/vendor/payments-page';
import { VendorSettingsPage } from './pages/vendor/settings-page';
import { VendorAgreementsPage } from './pages/vendor/agreements-page';
import { NotificationsPage } from './pages/notifications-page';
import { UserShell } from './pages/user/user-shell';
import { HomeTabPage } from './pages/user/home-tab-page';
import { FoodCafeTabPage } from './pages/user/food-cafe-tab-page';
import { ServicesTabPage } from './pages/user/services-tab-page';
import { HealthTabPage } from './pages/user/health-tab-page';
import { PetsListPage } from './pages/user/pets-list-page';
import { PetEditPage } from './pages/user/pet-edit-page';
import { DiscoverPage } from './pages/user/discover-page';
import { ShopPage } from './pages/user/shop-page';
import { ProductPage } from './pages/user/product-page';
import { SearchPage } from './pages/user/search-page';
import { VendorDetailPage } from './pages/user/vendor-detail-page';
import { BookPage } from './pages/user/book-page';
import { PlanPage } from './pages/user/plan-page';
import { TrainingPackagePage } from './pages/user/training-package-page';
import { MyBookingsPage } from './pages/user/my-bookings-page';
import { SavedPage } from './pages/user/saved-page';
import { AddressesPage } from './pages/user/addresses-page';
import { MyOrdersPage } from './pages/user/my-orders-page';
import { UserProfilePage } from './pages/user/profile-page';
import { AdoptBrowsePage } from './pages/user/adopt-browse-page';
import { AdoptPostPage } from './pages/user/adopt-post-page';
import { MyListingsPage } from './pages/user/my-listings-page';
import { MyAdoptionsPage } from './pages/user/my-adoptions-page';
import { AdoptListingDetailPage } from './pages/user/adopt-listing-detail-page';
import { use_session_store, type SessionRole } from './lib/session-store';

/**
 * Guard a subtree by login + role. On miss:
 *  - vendor → /login (preserves the standalone vendor-console entry + e2e)
 *  - user   → / (the chooser is the pet-owner front door; also avoids a
 *    sign-out race where the guard would otherwise beat navigate() to a
 *    different destination)
 */
function RequireRole({ role, children }: { role: SessionRole; children: React.ReactNode }) {
  const token = use_session_store((s) => s.onboarding_token);
  const session_role = use_session_store((s) => s.role);
  if (!token || session_role !== role) {
    return <Navigate to={role === 'vendor' ? '/login' : '/'} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/choose" element={<ChooserPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/user/login" element={<UserLoginPage />} />
      {/*
        Account deletion, public and unguarded on purpose.

        The store policies require an account holder to be able to ask for deletion from a
        plain URL with no app installed and nobody signed in — so this route sits beside the
        landing page rather than inside either console, and must stay out of RequireRole.
      */}
      <Route path="/request-account-deletion" element={<RequestAccountDeletionPage />} />

      {/* Vendor console */}
      <Route
        path="/onboarding"
        element={
          <RequireRole role="vendor">
            <OnboardingShell />
          </RequireRole>
        }
      >
        <Route index element={<Navigate to="start" replace />} />
        <Route path="start" element={<StartPage />} />
        <Route path="basic-info" element={<BasicInfoPage />} />
        <Route path="address" element={<AddressPage />} />
        <Route path="uploads" element={<UploadsPage />} />
        <Route path="module-details/cafe" element={<ModuleCafePage />} />
        <Route path="module-details/walker" element={<ModuleWalkerPage />} />
        <Route path="module-details/trainer" element={<ModuleTrainerPage />} />
        <Route path="module-details/groomer" element={<ModuleGroomerPage />} />
        <Route path="module-details/restaurant" element={<ModuleRestaurantPage />} />
        <Route path="module-details/pharmacy" element={<ModulePharmacyPage />} />
        <Route path="module-details/food" element={<ModuleFoodPage />} />
        <Route path="module-details/adoption" element={<ModuleAdoptionPage />} />
        <Route path="module-details/cremation" element={<ModuleCremationPage />} />
        <Route path="module-details/lab" element={<ModuleLabPage />} />
        <Route path="module-details/vet" element={<ModuleVetPage />} />
        <Route path="payout" element={<PayoutPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="submit" element={<SubmitPage />} />
      </Route>

      {/* Vendor workspace (post-onboarding) */}
      <Route
        path="/vendor/app"
        element={
          <RequireRole role="vendor">
            <VendorShell />
          </RequireRole>
        }
      >
        <Route index element={<VendorDashboardPage />} />
        <Route path="services" element={<VendorServicesPage />} />
        <Route path="dining" element={<DiningSetupPage />} />
        <Route path="offerings" element={<DiningOfferingsPage />} />
        <Route path="menu" element={<VendorMenuImagesPage />} />
        <Route path="availability" element={<VendorAvailabilityPage />} />
        <Route path="schedule" element={<VendorSchedulePage />} />
        <Route path="bookings" element={<VendorBookingsPage />} />
        <Route path="walk-plans" element={<VendorWalkPlansPage />} />
        <Route path="training-packages" element={<VendorTrainingPackagesPage />} />
        <Route path="packages" element={<VendorServicePackagesPage />} />
        <Route path="grooming-packages" element={<GroomingPackagesPage />} />
        <Route path="groom-reports" element={<GroomReportsPage />} />
        <Route path="orders" element={<VendorOrdersPage />} />
        <Route path="pharmacy-products" element={<VendorPharmacyProductsPage />} />
        <Route path="food-products" element={<VendorFoodProductsPage />} />
        <Route path="commerce-shipments" element={<CommerceShipmentsPage />} />
        <Route path="reviews" element={<VendorReviewsPage />} />
        <Route path="reports" element={<VendorReportsPage />} />
        <Route path="payments" element={<VendorPaymentsPage />} />
        <Route path="agreements" element={<VendorAgreementsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<VendorSettingsPage />} />
        <Route path="profile" element={<VendorProfilePage />} />
      </Route>

      {/* Pet-Owner (user) console */}
      <Route
        path="/app"
        element={
          <RequireRole role="user">
            <UserShell />
          </RequireRole>
        }
      >
        {/* Index stays on `pets` — existing e2e assert that post-login landing. The
            aggregated landing tabs below are reachable via the shell nav. */}
        <Route index element={<Navigate to="pets" replace />} />
        {/* Aggregated landing tabs — reference consumers of the app-tab endpoints. */}
        <Route path="home" element={<HomeTabPage />} />
        <Route path="food-cafe" element={<FoodCafeTabPage />} />
        <Route path="services" element={<ServicesTabPage />} />
        <Route path="health" element={<HealthTabPage />} />
        <Route path="pets" element={<PetsListPage />} />
        <Route path="pets/new" element={<PetEditPage />} />
        <Route path="pets/:id" element={<PetEditPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="discover/:id" element={<VendorDetailPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="shop" element={<ShopPage />} />
        <Route path="shop/p/:id" element={<ProductPage />} />
        <Route path="book/:serviceId" element={<BookPage />} />
        <Route path="plan/:packageId" element={<PlanPage />} />
        <Route path="package/:packageId" element={<TrainingPackagePage />} />
        <Route path="saved" element={<SavedPage />} />
        <Route path="addresses" element={<AddressesPage />} />
        <Route path="bookings" element={<MyBookingsPage />} />
        <Route path="orders" element={<MyOrdersPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="adopt" element={<AdoptBrowsePage />} />
        <Route path="adopt/new" element={<AdoptPostPage />} />
        <Route path="adopt/edit/:id" element={<AdoptPostPage />} />
        <Route path="adopt/mine" element={<MyListingsPage />} />
        <Route path="adopt/adoptions" element={<MyAdoptionsPage />} />
        <Route path="adopt/:id" element={<AdoptListingDetailPage />} />
        <Route path="profile" element={<UserProfilePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
